import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { useAdminSession, useAdminUiStore } from "../../app/providers";
import { pathForAdminEntityScreen } from "../../app/routes";
import type { Schemas } from "../workspace/api.generated";
import { IntegrationView } from "./components/IntegrationView";
import { useIntegrationMutation, useProviderConfigurations, useProviderCredentialHistory } from "./queries";
import type { IntegrationMutation } from "./queries";
import { parseConfigurationId, parseProviderActive, parseProviderMode, parseProviderPage } from "./schema";
import type { ProviderActiveFilter, ProviderModeFilter } from "./schema";

const SIZE = 20;
type Notice = { readonly tone: "information" | "danger"; readonly text: string };
function queryState(query: { isPending: boolean; isError: boolean }) { return query.isPending ? "loading" as const : query.isError ? "error" as const : "ready" as const; }
function paramsFor(input: { providerCode?: string; serviceType?: string; providerMode?: ProviderModeFilter; active?: ProviderActiveFilter; page?: number; configurationId?: number }) {
  const params = new URLSearchParams();
  if (input.providerCode) params.set("providerCode", input.providerCode);
  if (input.serviceType) params.set("serviceType", input.serviceType);
  if (input.providerMode) params.set("mode", input.providerMode);
  if (input.active) params.set("active", input.active);
  if (input.page) params.set("page", String(input.page));
  if (input.configurationId) params.set("configurationId", String(input.configurationId));
  return params;
}

export function IntegrationPage() {
  const location = useLocation(); const routeNavigate = useNavigate(); const [searchParams, setSearchParams] = useSearchParams(); const session = useAdminSession(); const rememberListContext = useAdminUiStore((value) => value.rememberListContext);
  const providerCode = (searchParams.get("providerCode") ?? "").slice(0, 40); const serviceType = (searchParams.get("serviceType") ?? "").slice(0, 40); const providerMode = parseProviderMode(searchParams.get("mode")); const active = parseProviderActive(searchParams.get("active")); const page = parseProviderPage(searchParams.get("page")); const configurationId = parseConfigurationId(searchParams.get("configurationId"));
  const criteria = { providerCode, serviceType, providerMode, active, page, size: SIZE }; const configurations = useProviderConfigurations(criteria); const selected = configurations.data?.items.find((row) => row.id === configurationId); const history = useProviderCredentialHistory(selected?.id); const mutation = useIntegrationMutation(); const [notice, setNotice] = useState<Notice>();
  const navigation = useAdminNavigation({ contextKey: "integrations", defaultListPath: "/integrations?page=0", originLabel: "연동 설정 목록" });
  const restorationNotice = useRestoreAdminListViewport("integrations", Boolean(configurations.data));

  useEffect(() => {
    if (configurationId) {
      routeNavigate(pathForAdminEntityScreen("29", configurationId), { replace: true });
      return;
    }
    const canonical = paramsFor({ providerCode, serviceType, providerMode, active, page, configurationId });
    if (canonical.toString() !== searchParams.toString()) setSearchParams(canonical, { replace: true });
  }, [active, configurationId, providerMode, page, providerCode, routeNavigate, searchParams, serviceType, setSearchParams]);

  const navigate = (next: { providerCode?: string; serviceType?: string; providerMode?: ProviderModeFilter; active?: ProviderActiveFilter; page?: number; configurationId?: number }) => {
    const params = paramsFor(next); setNotice(undefined);
    rememberListContext("integrations", { path: `${location.pathname}${params.size ? `?${params}` : ""}`, page: next.page ?? 0, filters: { providerCode: next.providerCode ?? "", serviceType: next.serviceType ?? "", providerMode: next.providerMode ?? "", active: next.active ?? "" } });
    setSearchParams(params);
    navigation.focusListResults();
  };
  const runMutation = async (input: IntegrationMutation) => {
    try { return await mutation.mutateAsync(input); }
    finally { if (input.kind === "credential") mutation.reset(); }
  };
  const complete = async (input: IntegrationMutation, result: unknown) => {
    if (input.kind === "create") {
      const saved = result as Schemas["ProviderConfigurationView"];
      setNotice({ tone: "information", text: `${saved.configurationName} · 서버 설정 ID ${saved.id}가 비활성 상태로 생성되었습니다.` });
      navigation.openDetail(pathForAdminEntityScreen("29", saved.id));
    }
    if (input.kind === "credential") setNotice({ tone: "information", text: `설정 ID ${input.id}의 인증 정보가 저장됐습니다. 원문 입력은 지웠고 마스킹 이력을 다시 조회했습니다.` });
    if (input.kind === "connection-test") {
      const tested = result as Schemas["ProviderConnectionTestResult"];
      setNotice({ tone: tested.successful ? "information" : "danger", text: `연결 테스트 ${tested.successful ? "성공" : "실패"} · ${tested.code} · ${tested.message}${tested.successful ? "" : " 활성화 상태는 변경하지 않았습니다."}` });
    }
    if (input.kind === "activate") {
      const saved = result as Schemas["ProviderConfigurationView"];
      setNotice({ tone: "information", text: `${saved.configurationName} · 서버 설정 ID ${saved.id}가 활성화되었습니다.` });
      setSearchParams(paramsFor({ providerCode: saved.providerCode, serviceType: saved.serviceType, providerMode: parseProviderMode(saved.mode), active: "true", configurationId: saved.id }));
    }
  };

  return <IntegrationView
    mode="list"
    providerCode={providerCode} serviceType={serviceType} providerMode={providerMode} active={active} page={page} size={SIZE}
    configurations={configurations.data} configurationsState={queryState(configurations)} selectedId={configurationId} selected={selected}
    history={history.data} historyState={selected ? queryState(history) : "idle"}
    canManage={(session.authSession?.authorities ?? []).includes("PROVIDER_MANAGE") && (session.authSession?.authorities ?? []).includes("ROLE_SUPER")}
    notice={restorationNotice
      ? { tone: "information", text: [notice?.text, restorationNotice].filter(Boolean).join(" ") }
      : notice}
    onFilterChange={(next) => navigate({ ...next, page: 0 })}
    onPageChange={(nextPage) => navigate({ providerCode, serviceType, providerMode, active, page: nextPage })}
    onSelect={(id) => navigate({ providerCode, serviceType, providerMode, active, page, configurationId: id })}
    detailHref={(id) => pathForAdminEntityScreen("29", id)}
    onOpenDetail={(id) => navigation.openDetail(pathForAdminEntityScreen("29", id), `integration:${id}:detail`)}
    onRetryList={() => void configurations.refetch()} onRetryHistory={() => void history.refetch()}
    onMutation={runMutation} onMutationComplete={complete}
  />;
}
